import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import ExpiryDatePicker from './ExpiryDatePicker';
import { PlusIcon, NewspaperIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface Newsletter {
  id: string;
  title: string;
  date: string;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  lastModified: string;
}

const NewsletterManager: React.FC = () => {
  const [newsletters, setNewsletters] = useState<Newsletter[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<Newsletter | null>(null);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    file: null as File | null,
    expiryDate: ''
  });

  useEffect(() => {
    loadNewsletters();
  }, []);

  const loadNewsletters = async () => {
    setLoading(true);
    try {
      const data = await apiService.getNewsletters();
      const newsletters = Array.isArray(data) ? data.filter(item => item !== null) as unknown as Newsletter[] : [];
      setNewsletters(newsletters);
    } catch (error) {
      console.error('Failed to load newsletters:', error);
      setNewsletters([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        alert('Please select a PDF file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      setUploadForm(prev => ({ ...prev, file }));
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!uploadForm.file || !uploadForm.title || !uploadForm.date) {
      alert('Please fill in all required fields and select a file');
      return;
    }

    setUploading(true);
    try {
      await apiService.uploadNewsletter(
        uploadForm.file,
        uploadForm.title,
        uploadForm.description,
        uploadForm.date,
        uploadForm.expiryDate || undefined
      );

      // Reset form
      setUploadForm({
        title: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
        file: null,
        expiryDate: ''
      });
      
      // Clear file input
      const fileInput = document.getElementById('newsletter-file') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      
      // Reload newsletters
      await loadNewsletters();
      
      alert('Newsletter uploaded successfully!');
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload newsletter. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (newsletter: Newsletter) => {
    setDeleteConfirm(newsletter);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      await apiService.deleteNewsletter(deleteConfirm.id);
      await loadNewsletters();
      setDeleteConfirm(null);
      alert('Newsletter deleted successfully');
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Failed to delete newsletter. Please try again.');
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm(null);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <PlusIcon className="h-5 w-5 mr-2" />
          Upload Newsletter
        </h3>
        
        <form onSubmit={handleUpload} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <input
                type="text"
                id="title"
                value={uploadForm.title}
                onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-church-blue"
                placeholder="e.g., March 2024 Newsletter"
                required
              />
            </div>
            
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                Date *
              </label>
              <input
                type="date"
                id="date"
                value={uploadForm.date}
                onChange={(e) => setUploadForm(prev => ({ ...prev, date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-church-blue"
                required
              />
            </div>
          </div>
          
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              value={uploadForm.description}
              onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-church-blue"
              rows={3}
              placeholder="Optional description of the newsletter content"
            />
          </div>
          
          <div>
            <label htmlFor="newsletter-file" className="block text-sm font-medium text-gray-700 mb-1">
              PDF File * (Max 10MB)
            </label>
            <input
              type="file"
              id="newsletter-file"
              accept=".pdf"
              onChange={handleFileChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-church-blue"
              required
            />
            {uploadForm.file && (
              <p className="text-sm text-gray-600 mt-1">
                Selected: {uploadForm.file.name} ({formatFileSize(uploadForm.file.size)})
              </p>
            )}
          </div>

          <ExpiryDatePicker
            value={uploadForm.expiryDate}
            onChange={(expiryDate) => setUploadForm(prev => ({ ...prev, expiryDate }))}
          />

          <button
            type="submit"
            disabled={uploading}
            className="bg-church-blue text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-church-blue disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? 'Uploading...' : 'Upload Newsletter'}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <NewspaperIcon className="h-5 w-5 mr-2" />
          Existing Newsletters
        </h3>
        
        {loading ? (
          <p className="text-gray-600">Loading newsletters...</p>
        ) : newsletters.length === 0 ? (
          <p className="text-gray-600">No newsletters uploaded yet.</p>
        ) : (
          <div className="space-y-3">
            {newsletters.map((newsletter) => (
              <div key={newsletter.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{newsletter.title}</h4>
                  <p className="text-sm text-gray-600">
                    {newsletter.fileName} • {formatFileSize(newsletter.fileSize)} • 
                    {new Date(newsletter.date).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <a
                    href={newsletter.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-church-blue hover:text-blue-700 text-sm font-medium"
                  >
                    View PDF
                  </a>
                  <button
                    onClick={() => handleDelete(newsletter)}
                    className="text-red-600 hover:text-red-800 p-1"
                    title="Delete newsletter"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center mb-4">
              <XMarkIcon className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">
                Confirm Deletion
              </h3>
            </div>
            
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete "{deleteConfirm.title}"? This action cannot be undone.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={confirmDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded transition-colors duration-200"
              >
                Delete
              </button>
              <button
                onClick={cancelDelete}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium py-2 px-4 rounded transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsletterManager;